/**
 * LayerRegistry — centrale plek voor alle Leaflet kaartlagen op /app.
 *
 * Vervangt de hard-coded laag-init in app-core.js. Definities komen uit de
 * map_layers tabel via /api/v1/map-layers en worden server-side ingebed in
 * window.__APP.mapLayers (zie app.php). Frontend rendert hieruit:
 *   - dynamische checkbox-UI gegroepeerd per categorie
 *   - factory per type (geojson_url, wms, tile_xyz, stations, nl_alert, helicopters)
 *   - lifecycle (load, show, hide) + preference sync
 *
 * Backward compat: bij init wordt een laag's initial visibility bepaald door (in volgorde):
 *   1. localStorage 'mapLayer:<slug>'
 *   2. legacy localStorage key uit meta.legacy_pref (bv. 'vrVisible', 'layer_bw')
 *   3. default_visible uit registry
 */
import { savePref } from './preferences.js';

const CATEGORY_ORDER = ['grenzen', 'kazernes', 'live-tracking', 'achtergrond', 'overig'];
const CATEGORY_LABELS = {
    'grenzen': 'Grenzen',
    'kazernes': 'Posten & locaties',
    'live-tracking': 'Live tracking',
    'achtergrond': 'Achtergrond',
    'overig': 'Overig',
};

export class LayerRegistry {
    /**
     * @param {object} options
     * @param {L.Map} options.map - Leaflet map instance
     * @param {Array} options.definitions - layer definitions from server
     * @param {object} options.handlers - per-type render handlers (provided by app-core)
     */
    constructor({ map, definitions, handlers }) {
        this.map = map;
        this.definitions = Array.isArray(definitions) ? definitions : [];
        this.handlers = handlers || {};
        // slug -> { def, layerInstance, isVisible }
        this.state = new Map();
    }

    /**
     * Build the layer-toggle UI inside `container` and wire up checkbox handlers.
     * Returns a list of slugs that were initialized.
     */
    bootstrap(container) {
        if (!container) return [];
        container.innerHTML = '';

        // Group by category, preserving server-side sort_order within each group.
        const byCategory = new Map();
        for (const def of this.definitions) {
            const cat = def.category || 'overig';
            if (!byCategory.has(cat)) byCategory.set(cat, []);
            byCategory.get(cat).push(def);
        }

        const orderedCats = [
            ...CATEGORY_ORDER.filter(c => byCategory.has(c)),
            ...[...byCategory.keys()].filter(c => !CATEGORY_ORDER.includes(c)),
        ];

        const initialized = [];
        for (const cat of orderedCats) {
            const labelEl = document.createElement('div');
            labelEl.className = 'mc-layer-group-label';
            labelEl.textContent = CATEGORY_LABELS[cat] || cat;
            container.appendChild(labelEl);

            for (const def of byCategory.get(cat)) {
                this._renderCheckbox(container, def);
                initialized.push(def.slug);
            }
        }

        // Trigger initial loads for all visible layers (deferred slightly so map is ready)
        setTimeout(() => {
            for (const slug of initialized) {
                const entry = this.state.get(slug);
                if (entry && entry.isVisible) this._show(slug);
            }
        }, 0);

        return initialized;
    }

    _renderCheckbox(container, def) {
        const wrap = document.createElement('label');
        wrap.className = 'mc-layer-item';
        wrap.dataset.layerSlug = def.slug;

        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.id = 'layerCb-' + def.slug;
        const initialVisible = this._initialVisibility(def);
        cb.checked = initialVisible;

        wrap.appendChild(cb);
        if (def.icon_html) {
            const iconWrap = document.createElement('span');
            iconWrap.className = 'mc-layer-icon';
            iconWrap.innerHTML = def.icon_html;
            wrap.appendChild(iconWrap);
        }
        wrap.appendChild(document.createTextNode(' ' + def.label));

        // Optionele extra (bv. NL-Alert badge die elders gevuld wordt)
        if (def.slug === 'nl-alert') {
            const badge = document.createElement('span');
            badge.className = 'mc-nl-count';
            badge.id = 'nlAlertCount';
            badge.style.display = 'none';
            wrap.appendChild(badge);
        }

        container.appendChild(wrap);

        this.state.set(def.slug, {
            def,
            layerInstance: null,
            isVisible: initialVisible,
        });

        cb.addEventListener('change', () => {
            this.setVisible(def.slug, cb.checked, /* fromUser */ true);
        });
    }

    _initialVisibility(def) {
        const direct = localStorage.getItem('mapLayer:' + def.slug);
        if (direct !== null) return direct === '1';

        const meta = def.meta || {};
        if (meta.legacy_pref) {
            const legacy = localStorage.getItem(meta.legacy_pref);
            if (legacy !== null) {
                // 'layer_nlalert' uses inverse default ('!== 0' => true)
                if (def.slug === 'nl-alert') return legacy !== '0';
                return legacy === '1';
            }
        }
        return !!def.default_visible;
    }

    /**
     * Programmatically toggle a layer.
     */
    setVisible(slug, visible, fromUser = false) {
        const entry = this.state.get(slug);
        if (!entry) return;
        entry.isVisible = !!visible;
        if (fromUser) {
            savePref('mapLayer:' + slug, visible ? '1' : '0');
        }
        if (visible) this._show(slug);
        else this._hide(slug);
    }

    _show(slug) {
        const entry = this.state.get(slug);
        if (!entry) return;
        const handler = this.handlers[entry.def.type];
        if (!handler || typeof handler.show !== 'function') {
            console.warn('LayerRegistry: no show handler for type', entry.def.type);
            return;
        }
        try {
            handler.show(entry.def, this.map, entry);
        } catch (err) {
            console.error('LayerRegistry: show failed for', slug, err);
        }
    }

    _hide(slug) {
        const entry = this.state.get(slug);
        if (!entry) return;
        const handler = this.handlers[entry.def.type];
        if (!handler || typeof handler.hide !== 'function') return;
        try {
            handler.hide(entry.def, this.map, entry);
        } catch (err) {
            console.error('LayerRegistry: hide failed for', slug, err);
        }
    }

    /**
     * Lookup helper for app-core consumers (e.g. NL-Alert refresh tick).
     */
    getEntry(slug) {
        return this.state.get(slug) || null;
    }

    isVisible(slug) {
        const entry = this.state.get(slug);
        return !!(entry && entry.isVisible);
    }

    /**
     * Look up a definition by meta property — used to find e.g. the helicopters layer
     * regardless of slug renames.
     */
    findBy(predicate) {
        for (const entry of this.state.values()) {
            if (predicate(entry.def)) return entry;
        }
        return null;
    }
}
